import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Plus, Users, Layers, MoreVertical, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AppHeader } from "@/components/app/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth-context";
import { type KBoard } from "@/lib/kanban";
import { useMyBoards, useBoardCounts } from "@/hooks/use-kanban";

export const Route = createFileRoute("/_app/boards/")({ component: BoardsListPage });

function BoardsListPage() {
  const { role, user } = useAuth();
  const isAdmin = role === "admin";
  const { data: boards = [], isLoading } = useMyBoards();
  const { data: counts } = useBoardCounts(boards);
  const [boardToDelete, setBoardToDelete] = useState<KBoard | null>(null);

  const groups = useMemo(() => {
    const dept: KBoard[] = [];
    const personalMine: KBoard[] = [];
    const personalSharedWithMe: KBoard[] = [];
    const archived: KBoard[] = [];
    for (const b of boards) {
      if (b.status === "archived") {
        archived.push(b);
        continue;
      }
      if (b.board_type === "department") dept.push(b);
      else if (b.owner_id === user?.id) personalMine.push(b);
      else personalSharedWithMe.push(b);
    }
    return { dept, personalMine, personalSharedWithMe, archived };
  }, [boards, user?.id]);

  return (
    <>
      <AppHeader
        subtitle="Plan your team's work on Kanban boards"
        ctaLabel="+ New Board"
        onCta={() => {
          window.location.href = "/boards/new";
        }}
      />
      <div className="px-[22px] pb-6">
        <Section title="Department Boards">
          {isLoading ? (
            <SkeletonGrid />
          ) : groups.dept.length === 0 ? (
            <Empty text="No department boards yet." />
          ) : (
            <Grid>
              {groups.dept.map((b) => (
                <BoardCard
                  key={b.id}
                  board={b}
                  counts={counts?.get(b.id)}
                  isAdmin={isAdmin}
                  onDelete={() => setBoardToDelete(b)}
                />
              ))}
            </Grid>
          )}
        </Section>
        <Section title="Personal Boards">
          {isLoading ? (
            <SkeletonGrid />
          ) : groups.personalMine.length + groups.personalSharedWithMe.length === 0 ? (
            <Empty text="No personal boards yet." />
          ) : (
            <Grid>
              {[...groups.personalMine, ...groups.personalSharedWithMe].map((b) => (
                <BoardCard
                  key={b.id}
                  board={b}
                  counts={counts?.get(b.id)}
                  isAdmin={isAdmin}
                  onDelete={() => setBoardToDelete(b)}
                />
              ))}
            </Grid>
          )}
        </Section>
        {isAdmin && groups.archived.length > 0 && (
          <Section title="Archived (Admin)">
            <Grid>
              {groups.archived.map((b) => (
                <BoardCard
                  key={b.id}
                  board={b}
                  counts={counts?.get(b.id)}
                  isAdmin
                  onDelete={() => setBoardToDelete(b)}
                />
              ))}
            </Grid>
          </Section>
        )}
      </div>
      {boardToDelete && (
        <DeleteBoardDialog
          board={boardToDelete}
          onClose={() => setBoardToDelete(null)}
        />
      )}
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-6">
      <h2 className="mb-2 text-[13px] font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h2>
      {children}
    </div>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">{children}</div>;
}

function SkeletonGrid() {
  return (
    <Grid>
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="h-28 animate-pulse rounded-xl bg-muted/40" />
      ))}
    </Grid>
  );
}

function Empty({ text }: { text: string }) {
  return (
    <div className="rounded-xl border border-dashed border-border p-8 text-center text-[12px] text-muted-foreground">
      {text}
    </div>
  );
}

function BoardCard({
  board,
  counts,
  isAdmin,
  onDelete,
}: {
  board: KBoard;
  counts?: { members: number; cards: number };
  isAdmin?: boolean;
  onDelete?: () => void;
}) {
  const archived = board.status === "archived";
  const inner = (
    <div
      className={cn(
        "group relative rounded-xl border bg-card p-4 shadow-sm transition-all",
        archived
          ? "border-slate-200 bg-slate-50/50 opacity-80"
          : "border-border hover:shadow-md hover:-translate-y-0.5",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-[14px] font-bold leading-tight">{board.name}</h3>
        <div className="flex items-center gap-1">
          {archived && (
            <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
              Archived
            </span>
          )}
          {isAdmin && onDelete && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  className="rounded p-1 text-muted-foreground hover:bg-muted"
                  aria-label="Board actions"
                >
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                <DropdownMenuItem
                  className="text-red-600 focus:text-red-700"
                  onSelect={(e) => {
                    e.preventDefault();
                    onDelete();
                  }}
                >
                  <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete board
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      <div className="mt-1 text-[10px] uppercase tracking-wide text-muted-foreground">
        {board.board_type === "department" ? `Department · ${board.department ?? ""}` : "Personal"}
      </div>
      <div className="mt-3 flex items-center gap-4 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <Users className="h-3 w-3" /> {counts?.members ?? 0}
        </span>
        <span className="inline-flex items-center gap-1">
          <Layers className="h-3 w-3" /> {counts?.cards ?? 0} cards
        </span>
      </div>
    </div>
  );
  if (archived) return <div>{inner}</div>;
  return (
    <Link to="/boards/$id" params={{ id: board.id }} className="block">
      {inner}
    </Link>
  );
}

function DeleteBoardDialog({ board, onClose }: { board: KBoard; onClose: () => void }) {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const armed = confirm.trim() === board.name.trim();
  const submit = async () => {
    if (!armed) return;
    setBusy(true);
    const { error } = await supabase.from("kanban_boards").delete().eq("id", board.id);
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(`Deleted "${board.name}"`);
    qc.invalidateQueries({ queryKey: ["kanban"] });
    onClose();
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-red-600">Delete board permanently?</DialogTitle>
          <DialogDescription>
            This permanently deletes <strong>{board.name}</strong> and ALL its cards, fields,
            and comments. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          <label className="text-[12px] font-medium">
            Type <span className="font-mono text-red-600">{board.name}</span> to confirm
          </label>
          <Input
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={board.name}
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={!armed || busy}
            onClick={submit}
            className="bg-red-600 hover:bg-red-700"
          >
            {busy ? "Deleting…" : "Delete board"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

void Plus;
